import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuMessageSquareWarning, LuShieldCheck, LuReceipt } from 'react-icons/lu';
import api from '../../services/api';
import { fechaAR } from '../../utils/dateUtils';
import ReclamoSheet from './ReclamoSheet';

// Reclamos en la ficha de una visita (28-oct-2026):
// - visita de reclamo: de qué trabajo viene, si va sin cargo, y la causa (el técnico la elige al cerrar; el
//   admin además puede cambiar el cobro a mano);
// - trabajo hecho: "Volver por un reclamo", o el aviso de que ya hay uno agendado.
export const CAUSAS = [
    { id: 'PIEZA', label: 'Filtro o pieza mal colocada', cobro: 'GARANTIA' },
    { id: 'AJUSTE', label: 'Faltó ajustar (canilla, conexión)', cobro: 'GARANTIA' },
    { id: 'SIN_FALLA', label: 'No tenía falla', cobro: 'GARANTIA' },
    { id: 'FALLA_NUEVA', label: 'Falla nueva del equipo', cobro: 'COBRA' },
    { id: 'MAL_USO', label: 'Mal uso o golpe', cobro: 'COBRA' },
];

export function CausaReclamo({ valor, onElegir }) {
    const chip = (on) => `min-h-10 px-3 py-1.5 rounded-xl text-label font-black border text-left active:scale-95 ${on ? 'border-brand-red bg-[rgba(232,66,47,0.10)] text-ink' : 'border-black/10 dark:border-white/10 text-secondary'}`;
    return (
        <div className="space-y-1.5">
            <p className="text-caption font-bold text-muted">¿Por qué volvió a fallar?</p>
            <div className="flex flex-wrap gap-1.5">
                {CAUSAS.map(c => (
                    <button key={c.id} type="button" onClick={() => onElegir(c.label)} aria-pressed={valor === c.label} className={chip(valor === c.label)}>
                        {c.label}<span className="block text-caption font-bold text-muted">{c.cobro === 'GARANTIA' ? 'sin cargo' : 'se cobra'}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}

export default function ReclamoEnFicha({ orden, soyAdmin = true, onCambio, onActualizar }) {
    const [abierto, setAbierto] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const r = orden.reclamo;

    const guardar = async (datos) => {
        setGuardando(true);
        try {
            const res = await api.patch(`/ordenes/${orden.id}/reclamo`, datos);
            onActualizar?.(res.data);
            onCambio?.();
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo guardar'); }
        finally { setGuardando(false); }
    };

    if (r) {
        const sinCargo = r.cobro === 'GARANTIA';
        return (
            <div className={`rounded-2xl p-3.5 space-y-3 border ${sinCargo ? 'border-[var(--etapa-listo)]/40 bg-[rgba(22,163,74,0.07)]' : 'border-brand-red/30 bg-[rgba(232,66,47,0.06)]'}`}>
                <div className="flex items-start gap-2.5">
                    <LuMessageSquareWarning size={18} className="shrink-0 mt-0.5 text-brand-red" />
                    <div className="min-w-0 flex-1">
                        <p className="text-body font-black text-ink">Reclamo: {r.motivo}</p>
                        <p className="text-caption text-secondary">
                            Del trabajo del {fechaAR(r.fechaOriginal)}{r.hechoOriginal ? ` (${r.hechoOriginal})` : ''}
                        </p>
                        {r.garantiaHasta && <p className="text-caption text-muted">Garantía hasta el {fechaAR(r.garantiaHasta)}</p>}
                    </div>
                </div>
                {soyAdmin ? (
                    <div className="grid grid-cols-2 gap-2">
                        {[['GARANTIA', 'Sin cargo', LuShieldCheck], ['COBRA', 'Se cobra', LuReceipt]].map(([v, txt, Icon]) => (
                            <button key={v} type="button" disabled={guardando} onClick={() => r.cobro !== v && guardar({ cobro: v })} aria-pressed={r.cobro === v}
                                className={`h-10 rounded-xl text-label font-black inline-flex items-center justify-center gap-1.5 border-2 active:scale-95 disabled:opacity-50 ${r.cobro === v
                                    ? (v === 'GARANTIA' ? 'border-[var(--etapa-listo)] text-ink' : 'border-brand-red text-ink') : 'border-black/10 dark:border-white/10 text-muted'}`}>
                                <Icon size={15} /> {txt}
                            </button>
                        ))}
                    </div>
                ) : (
                    <p className={`text-caption font-black ${sinCargo ? 'text-[var(--etapa-listo-tx)]' : 'text-brand-red'}`}>{sinCargo ? 'Sin cargo (garantía)' : 'Se cobra'}</p>
                )}
                <CausaReclamo valor={r.causa} onElegir={(c) => !guardando && c !== r.causa && guardar({ causa: c })} />
            </div>
        );
    }

    if (orden.estado !== 'COMPLETADA' || !soyAdmin) return null;
    if (orden.reclamoId) return (
        <p className="flex items-center gap-2 px-1 text-caption font-bold text-brand-red">
            <LuMessageSquareWarning size={15} /> Ya hay un reclamo agendado de este trabajo
        </p>
    );
    // 29-oct-2026: los reclamos ya hechos quedan como registro, sin decir que hay uno agendado
    const hechos = orden.reclamosHechos || [];
    return (
        <>
            {hechos.length > 0 && (
                <p className="flex items-center gap-2 px-1 text-caption font-bold text-muted">
                    <LuMessageSquareWarning size={15} /> Tuvo {hechos.length === 1 ? 'un reclamo' : `${hechos.length} reclamos`}: {hechos.map(h => fechaAR(h.fecha)).join(', ')}
                </p>
            )}
            <button type="button" onClick={() => setAbierto(true)}
                className="w-full h-11 rounded-xl border border-black/10 dark:border-white/10 text-ink text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95">
                <LuMessageSquareWarning size={15} /> Volver por un reclamo
            </button>
            {abierto && <ReclamoSheet orden={orden} onCerrar={() => setAbierto(false)}
                onListo={() => { setAbierto(false); onActualizar?.({ reclamoId: true }); onCambio?.(); }} />}
        </>
    );
}

// En el detalle de un trabajo (Trabajos): busca la visita que lo cerró para ofrecer el reclamo
export function ReclamoDeTrabajo({ ordenId, onCambio }) {
    const [orden, setOrden] = React.useState(null);
    React.useEffect(() => {
        if (!ordenId) return;
        api.get(`/ordenes/${ordenId}`).then(r => setOrden(r.data)).catch(() => setOrden(null));
    }, [ordenId]);
    if (!orden) return null;
    return <ReclamoEnFicha orden={orden} onCambio={onCambio} onActualizar={(d) => setOrden(o => ({ ...o, ...d }))} />;
}
